/** XeAgents.ai**/
import { PhoneIncoming, CheckCircle2, Users, ArrowRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const steps = [
  {
    number: "01",
    key: "call",
    icon: PhoneIncoming,
    gradient: "from-blue-600 to-indigo-600",
    glowColor: "blue",
  },
  {
    number: "02",
    key: "complete",
    icon: CheckCircle2,
    gradient: "from-violet-600 to-purple-600",
    glowColor: "violet",
  },
  {
    number: "03",
    key: "handoff",
    icon: Users,
    gradient: "from-emerald-600 to-teal-600",
    glowColor: "emerald",
  },
];

export function HowItWorks() {
  const { t } = useTranslation("landing");
  return (
    <section
      className="py-16 sm:py-20 md:py-28 lg:py-36 relative overflow-hidden"
      data-testid="section-how-it-works"
    >
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(99,102,241,0.08),transparent)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(99,102,241,0.15),transparent)]" />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 sm:mb-20 md:mb-24"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-100 dark:border-indigo-800/50 mb-6"
          >
            <Sparkles className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
              {t("how.badge")}
            </span>
          </motion.div>

          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-5"
            data-testid="heading-how-it-works"
          >
            <span className="text-slate-900 dark:text-white">{t("how.titleA")}</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400">
              {t("how.titleB")}
            </span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            {t("how.subtitle")}
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative max-w-6xl mx-auto">
          {/* Connecting line (desktop only) */}
          <div className="hidden lg:block absolute top-[140px] left-[16.67%] right-[16.67%] h-[2px]">
            <div className="w-full h-full bg-gradient-to-r from-blue-200 via-violet-200 to-emerald-200 dark:from-blue-800 dark:via-violet-800 dark:to-emerald-800 rounded-full" />
            <motion.div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 via-violet-500 to-emerald-500 rounded-full"
              initial={{ width: "0%" }}
              whileInView={{ width: "100%" }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, delay: 0.5, ease: "easeInOut" }}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-10 lg:gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                className="relative group"
                data-testid={`card-step-${index}`}
              >
                {/* Arrow between cards (desktop only) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:flex absolute -right-3 top-[140px] z-10 -translate-y-1/2">
                    <motion.div
                      initial={{ opacity: 0, x: -8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.8 + index * 0.3 }}
                    >
                      <ArrowRight className="h-5 w-5 text-slate-400 dark:text-slate-600" />
                    </motion.div>
                  </div>
                )}

                <div className="relative bg-white dark:bg-slate-900/80 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-slate-700/50 p-6 sm:p-8 h-full transition-all duration-500 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-xl hover:shadow-slate-200/50 dark:hover:shadow-slate-900/50 backdrop-blur-sm">
                  {/* Glow effect on hover */}
                  <div
                    className={`absolute -inset-[1px] rounded-2xl sm:rounded-3xl bg-gradient-to-br ${step.gradient} opacity-0 group-hover:opacity-[0.08] transition-opacity duration-500 -z-10 blur-sm`}
                  />

                  {/* Step number + Icon row */}
                  <div className="flex items-center justify-between mb-6 sm:mb-8">
                    <div
                      className={`h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:shadow-xl transition-all duration-500`}
                      data-testid={`icon-step-${index}`}
                    >
                      <step.icon className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                    </div>
                    <span
                      className="text-6xl sm:text-7xl font-black text-slate-100 dark:text-slate-800/80 group-hover:text-slate-200 dark:group-hover:text-slate-700/80 transition-colors duration-500 select-none"
                      data-testid={`number-step-${index}`}
                    >
                      {step.number}
                    </span>
                  </div>

                  {/* Badge */}
                  <div className="mb-4">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${step.gradient} text-white`}
                    >
                      {t(`how.${step.key}.badge`)}
                    </span>
                  </div>

                  {/* Text */}
                  <h3
                    className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-1"
                    data-testid={`title-step-${index}`}
                  >
                    {t(`how.${step.key}.title`)}
                  </h3>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
                    {t(`how.${step.key}.subtitle`)}
                  </p>
                  <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                    {t(`how.${step.key}.description`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default HowItWorks;